'use strict';

import * as domUtils from '../../util/dom';
import Sizzle from 'sizzle';
import { STATE_CLASS, PROPERTY_CLASS } from './ui-constants';
import LanguageDialog from "./language-dialog";
import config from '../../config';


const NAMESPACE = 'ui-language-button';

const CLASSNAMES = {
    captionsText: 'svp_js-controls-captions--text',
    languageButton: 'svp_js-controls-btn--language',
    captionsLabel: 'svp_ui-controls__captions-label'
};
const CAPTIONS_STORAGE_KEY = "captionsOn";

const instances = new Map();

class LanguageButton {

    constructor(api) {
        this.api = api;
        this.languageDialog = new LanguageDialog(api, this);
        this.dialogVisible = false;
    }

    _getContainer() {
        return this._getElement('svp_ui-controls__button-captions-container');
    }

    _getElement(className) {
        return this.api && Sizzle('.' + className, this.api.element())[0];
    }

    _getLanguageButton() {
        return this._getElement(CLASSNAMES.languageButton);
    }

    _toggleButtonState() {
        if (!this.dialogVisible) {
            this._createDialog();
        } else {
            this.destroyDialog();
        }
    }

    _createDialog() {
        this.dialogElement = this.languageDialog.create();
        this._getContainer().appendChild(this.dialogElement);
        this.dialogVisible = true;
        this._getLanguageButton().setAttribute('aria-expanded', true);
        this.api.fire('languageDialog', { visible: true });

        domUtils.addEvents(this._getContainer(), 'focusout', (e) => {
            // In a focusout event, the relatedTarget is the element gaining focus. The following
            // will close the dialog when focus is moved to an element that is not a descendant of
            // the language button container.
            if (e.relatedTarget !== null && !this._getContainer().contains(e.relatedTarget)) {
                this.destroyDialog();
            }

            // By preventing the parent container from getting the focus event, we make sure that
            // the controls will stay visible.
            e.stopPropagation();
        });

        domUtils.addEvents(this.dialogElement, 'keyup', (e) => {
            if (domUtils.getKeyFromKeyEvent(e) === 'escape') {
                this.destroyDialog();
            }
        }, NAMESPACE);
    }

    _isSmallScreen() {
        return domUtils.hasClass(this.api.element(), 'svp_container--small');
    }

    destroyDialog() {
        if (this.dialogVisible) {
            domUtils.removeEvents(this._getContainer(), 'focusout', NAMESPACE);
            domUtils.removeEvents(this.dialogElement, 'keyup', NAMESPACE);

            this.dialogVisible = false;
            this._getContainer().removeChild(this.dialogElement);
            this.dialogElement = undefined;
            this._getLanguageButton().setAttribute('aria-expanded', false);
            this.api.fire('languageDialog', { visible: false });
        }
    }

    createElement() {
        return domUtils.fromString(
            `<div class="${PROPERTY_CLASS.controlsHideInitially} svp_ui-controls__button-captions-container svp_ui-controls--hide-on-error">
					<button class="${CLASSNAMES.languageButton} svp_ui-controls__button-captions"
                        aria-expanded="false"
						title="Inställningar"
						tabindex="0">
						<span class="svp_ui-controls__hidden ${CLASSNAMES.captionsLabel}">Inställningar</span>
					</button>
				</div>`);
    }

    reset() {
        this.dialogVisible = false;
        let languageButton = this._getLanguageButton();

        if (languageButton) {
            domUtils.removeClass(languageButton, [STATE_CLASS.visible]);
        }
    }

    bindEvents(api) {
        const updateCaptions = () => {
            if (api.getTextTracks() && api.getTextTracks().length > 0) {
                let shouldShow = domUtils.getStoredData(CAPTIONS_STORAGE_KEY) === 'true';
                api.showCaptions(shouldShow);
            }
        };
        api.on(NAMESPACE + '.captionsloaded', () => {
            domUtils.addClass(this._getLanguageButton(), STATE_CLASS.visible);
            updateCaptions();
        });

        api.on(NAMESPACE + '.audiotracksloaded', () => {
            domUtils.addClass(this._getLanguageButton(), STATE_CLASS.visible);
        });

        api.on(NAMESPACE + '.ready', () => {
            updateCaptions();
        });

        domUtils.addEvents(this._getLanguageButton(), 'touchstart, mousedown', (e) => {
            e.stopPropagation();
            e.preventDefault();
        }, NAMESPACE);

        domUtils.addEvents(this._getLanguageButton(), 'click', (e) => {
            e.stopPropagation();
            e.preventDefault();

            // Yes, this closes the dialog on big screens since it's already opened on hover but that is probably ok
            this._toggleButtonState();
        }, NAMESPACE);


        domUtils.addEvents(this._getLanguageButton(), 'touchend', (e) => {
            e.stopPropagation();
            e.preventDefault();
            this._toggleButtonState();
        }, NAMESPACE);

        domUtils.addEvents(this._getContainer(), 'mouseenter', (e) => {
            if (this._isSmallScreen()) {
                return;
            }
            if (!this.dialogVisible) {
                this._createDialog();
            }
        }, NAMESPACE);

        domUtils.addEvents(this._getContainer(), 'mouseleave', (e) => {
            if (this._isSmallScreen()) {
                return;
            }
            this.destroyDialog();
        }, NAMESPACE);

        domUtils.addEvents(this._getLanguageButton(), 'keyup', (e) => {
            const keyName = domUtils.getKeyFromKeyEvent(e);

            // Prevent to pause video if pressing enter on the language button
            if (keyName === 'enter') {
                e.stopPropagation();
                e.preventDefault();

                return true;
            }

            return false;
        }, NAMESPACE);
    }

    destroy() {
        const languageButton = Sizzle('.' + CLASSNAMES.languageButton, this.api.element())[0];
        if (languageButton) {
            domUtils.removeEvents(languageButton, 'mousedown, touchstart, click, touchend, keyup, mouseenter, mouseleave', NAMESPACE);
            languageButton.parentElement.removeChild(languageButton);
        }
        this.destroyDialog();

        this.api.off(NAMESPACE + '.audiotracksloaded');
        this.api.off(NAMESPACE + '.captionsloaded');
        this.api.off(NAMESPACE + '.ready');
    }
}

module.exports = {
    create: (api) => {
        const languageButton =  new LanguageButton(api);
        instances.set(api, languageButton);

        return languageButton;
    },
    destroy: (api) => {
        const languageButton = instances.get(api);
        if (languageButton) {
            instances.delete(api);
            languageButton.destroy();
        }
    }
};



// WEBPACK FOOTER //
// ./src/js/plugins/ui/language-button.js